import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { Me, Site, TenantSummary } from '@iot-dashboard/shared';
import { DEMO_MODE } from '../config';
import { activeSectionKey, type NavSection } from './nav';

interface Props {
  me: Me;
  tenant?: TenantSummary;
  sites?: Site[];
  sections: NavSection[];
  open: boolean;
  onNavigate: () => void;
}

export function Sidebar({ me, tenant, sites, sections, open, onNavigate }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const active = activeSectionKey(sections, location.pathname);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (active) setExpanded((e) => ({ ...e, [active]: true }));
  }, [active]);

  const siteParam = params.get('site') ?? '';
  const withSite = (to: string) => (siteParam && to.startsWith('/t/') ? `${to}?site=${encodeURIComponent(siteParam)}` : to);

  return (
    <aside className={`sidebar${open ? ' sidebar--open' : ''}`} aria-label="Navigation">
      <div className="sidebar__selectors">
        <label className="selector">
          <span className="selector__label">Organization</span>
          {me.isMsp ? (
            <select
              value={tenant?.tenantId ?? '__msp'}
              onChange={(e) => navigate(e.target.value === '__msp' ? '/msp' : `/t/${e.target.value}/overview`)}
            >
              <option value="__msp">MSP portal – all tenants</option>
              {me.tenants.map((t) => (
                <option key={t.tenantId} value={t.tenantId}>
                  {t.name}
                  {t.status === 'suspended' ? ' (suspended)' : ''}
                </option>
              ))}
            </select>
          ) : (
            <span className="selector__static">{tenant?.name ?? me.tenants[0]?.name ?? '—'}</span>
          )}
        </label>
        {tenant ? (
          <label className="selector">
            <span className="selector__label">Network</span>
            <select
              value={siteParam}
              onChange={(e) => {
                const next = new URLSearchParams(params);
                if (e.target.value) next.set('site', e.target.value);
                else next.delete('site');
                setParams(next, { replace: true });
              }}
            >
              <option value="">All sites</option>
              {(sites ?? []).map((s) => (
                <option key={s.siteId} value={s.siteId}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <nav className="nav">
        {sections.map((s) => {
          const isActive = active === s.key;
          const isOpen = expanded[s.key] ?? isActive;
          const Icon = s.icon;
          return (
            <div key={s.key} className={`nav__section${isActive ? ' nav__section--active' : ''}${isOpen ? ' nav__section--open' : ''}`}>
              <button type="button" className="nav__section-btn" onClick={() => setExpanded((e) => ({ ...e, [s.key]: !isOpen }))} aria-expanded={isOpen}>
                <Icon size={17} />
                {s.label}
                <ChevronDown size={14} className="nav__chevron" />
              </button>
              {isOpen ? (
                <div className={`nav__groups${s.configure.length === 0 ? ' nav__groups--single' : ''}`}>
                  <div>
                    <div className="nav__group-title">Monitor</div>
                    {s.monitor.map((l) => (
                      <NavLink key={l.to} to={withSite(l.to)} end={l.end} className={({ isActive: a }) => `nav__link${a ? ' nav__link--active' : ''}`} onClick={onNavigate}>
                        {l.label}
                      </NavLink>
                    ))}
                  </div>
                  {s.configure.length > 0 ? (
                    <div>
                      <div className="nav__group-title">Configure</div>
                      {s.configure.map((l) => (
                        <NavLink key={l.to} to={withSite(l.to)} end={l.end} className={({ isActive: a }) => `nav__link${a ? ' nav__link--active' : ''}`} onClick={onNavigate}>
                          {l.label}
                        </NavLink>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>
      <div className="sidebar__footer">
        {DEMO_MODE ? 'Demo mode · sample data' : 'Sensors and cameras'}
      </div>
    </aside>
  );
}
